/**
 * useReviewWindow — opens the review queue in its own window and keeps it in
 * step with the task pane.
 *
 * Word gets an Office dialog (``review.html``) sized for reading. The pane
 * stays the single owner of the queue: it pushes a ``ReviewState`` to the
 * window on every change, and turns the window's clicks into the existing
 * flow calls (``useProposalReviewFlow``). Where Office dialogs or parent-to-
 * dialog messages are not available (an old Word build, the browser preview),
 * ``mode`` is ``"overlay"`` and the host renders the same ``ReviewWorkspace``
 * over the whole pane.
 *
 * It opens over the pane. Expand and Minimise switch between the two: Expand
 * opens the window over Word, Minimise closes it and lays the view over the
 * pane again.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SectionIssue } from "../../api/client";
import type { UseProposalReviewFlowResult } from "../hooks/useProposalReviewFlow";
import { parseMessage, type ReviewState, toReviewState, type WindowToParent } from "./reviewProtocol";
import type { ViewPlacement } from "./SizeToggle";

/** Window size, as a percentage of the screen: as much of Word as it allows. */
export const WINDOW_SIZE = { height: 92, width: 92 } as const;

export type ReviewWindowMode = "closed" | "dialog" | "overlay";

export interface ReviewWindowHandlers {
  onSelect: (id: string) => void;
  onApprove: (id: string, content?: string) => void;
  onReject: (id: string) => void;
  onRevise: (id: string, guidance: string) => void;
  onApproveAll: () => void;
  onStopAll: () => void;
  /** Undo the change just approved. */
  onUndo: () => void;
  onClose: () => void;
}

export interface UseReviewWindowResult extends ReviewWindowHandlers {
  mode: ReviewWindowMode;
  state: ReviewState;
  open: () => void;
  /** For the overlay: it is over the pane, and Expand opens the window. */
  placement: ViewPlacement;
  onResize?: () => void;
}

type PendingDecision =
  | { action: "approve"; id: string; content?: string }
  | { action: "reject"; id: string }
  | { action: "revise"; id: string; guidance: string };

/** True when this Word can open a dialog AND message it after it loads. */
export function dialogsSupported(): boolean {
  try {
    return (
      typeof Office !== "undefined" &&
      Boolean(Office.context?.ui?.displayDialogAsync) &&
      Boolean(Office.context?.requirements?.isSetSupported?.("DialogApi", "1.2"))
    );
  } catch {
    return false;
  }
}

export function reviewWindowUrl(loc: Pick<Location, "origin" | "pathname" | "search"> = window.location): string {
  // The pane's own page and query (brand), told to draw the window instead:
  // a host that rewrites unknown pages to the pane (production redirected
  // review.html to /review and served the pane, which asked for a login)
  // still serves the page the pane itself came from.
  const params = new URLSearchParams(loc.search);
  params.set("window", "review");
  return `${loc.origin}${loc.pathname}?${params.toString()}`;
}

export function useReviewWindow(
  flow: UseProposalReviewFlowResult,
  issuesById?: Readonly<Record<string, SectionIssue>>,
): UseReviewWindowResult {
  const [mode, setMode] = useState<ReviewWindowMode>("closed");
  const [approvingAll, setApprovingAll] = useState(false);
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const dialogRef = useRef<Office.Dialog | null>(null);
  const readyRef = useRef(false);
  const errorAtStartRef = useRef<string | null>(null);

  const state = useMemo(() => toReviewState(flow, issuesById, approvingAll), [flow, issuesById, approvingAll]);
  const undoRef = useRef(flow.undoLast);
  undoRef.current = flow.undoLast;
  const stateRef = useRef(state);
  stateRef.current = state;

  const send = useCallback((s: ReviewState) => {
    const dialog = dialogRef.current;
    if (!dialog || !readyRef.current) return;
    try {
      dialog.messageChild(JSON.stringify({ type: "state", state: s }));
    } catch {
      // The window was closed under us — the close event tidies up.
    }
  }, []);

  useEffect(() => {
    if (mode === "dialog") send(state);
  }, [mode, state, send]);

  // A decision on a change that is not the active one: bring it forward first,
  // then act once the flow has it in hand (the flow acts on its active item).
  const { active, phase, selectById, approve, reject, revise, queue, error } = flow;
  useEffect(() => {
    if (!pending || phase !== "reviewing") return;
    if (active?.proposal.id !== pending.id) {
      if (!selectById(pending.id)) setPending(null);
      return;
    }
    setPending(null);
    if (pending.action === "approve") void approve(pending.content);
    else if (pending.action === "reject") void reject();
    else revise(pending.guidance);
  }, [pending, phase, active, selectById, approve, reject, revise]);

  // Approve all: one change at a time, stopping at the first new error, and
  // never asking twice for the same change (if an approve did not take it off
  // the queue, something is wrong — stop rather than loop).
  const lastAllIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!approvingAll) return;
    const first = queue[0];
    if (!first) {
      setApprovingAll(false);
      return;
    }
    if (pending || phase !== "reviewing") return;
    if (error && error !== errorAtStartRef.current) {
      setApprovingAll(false);
      return;
    }
    if (first.proposal.id === lastAllIdRef.current) return;
    lastAllIdRef.current = first.proposal.id;
    setPending({ action: "approve", id: first.proposal.id });
  }, [approvingAll, pending, phase, queue, error]);

  const handlers: ReviewWindowHandlers = useMemo(
    () => ({
      onSelect: (id) => {
        if (stateRef.current.phase === "reviewing") selectById(id);
      },
      onApprove: (id, content) => setPending({ action: "approve", id, content }),
      onReject: (id) => setPending({ action: "reject", id }),
      onRevise: (id, guidance) => setPending({ action: "revise", id, guidance }),
      onApproveAll: () => {
        errorAtStartRef.current = stateRef.current.error;
        lastAllIdRef.current = null;
        setApprovingAll(true);
      },
      onStopAll: () => setApprovingAll(false),
      onUndo: () => void undoRef.current(),
      onClose: () => {
        try {
          dialogRef.current?.close();
        } catch {
          // already closed
        }
        dialogRef.current = null;
        readyRef.current = false;
        setMode("closed");
      },
    }),
    [selectById],
  );
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const onMessage = useCallback(
    (raw: unknown) => {
      const msg = parseMessage<WindowToParent>(raw);
      if (!msg) return;
      const h = handlersRef.current;
      switch (msg.type) {
        case "ready":
          readyRef.current = true;
          send(stateRef.current);
          return;
        case "select":
          h.onSelect(msg.id);
          return;
        case "approve":
          h.onApprove(msg.id, msg.content);
          return;
        case "reject":
          h.onReject(msg.id);
          return;
        case "revise":
          h.onRevise(msg.id, msg.guidance);
          return;
        case "approveAll":
          h.onApproveAll();
          return;
        case "stopAll":
          h.onStopAll();
          return;
        case "undo":
          h.onUndo();
          return;
        case "minimise":
          minimiseRef.current();
          return;
        case "close":
          h.onClose();
          return;
      }
    },
    [send],
  );

  const minimiseRef = useRef<() => void>(() => {});
  minimiseRef.current = () => {
    try {
      dialogRef.current?.close();
    } catch {
      // already closed
    }
    dialogRef.current = null;
    readyRef.current = false;
    setMode("overlay");
  };

  const openWindow = useCallback(() => {
    if (dialogRef.current) return;
    Office.context.ui.displayDialogAsync(reviewWindowUrl(), { ...WINDOW_SIZE, displayInIframe: true }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        // Blocked or unsupported here — read it in the pane instead.
        setMode("overlay");
        return;
      }
      const dialog = result.value;
      dialogRef.current = dialog;
      readyRef.current = false;
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if ("message" in arg) onMessage(arg.message);
      });
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        // Closed with the window's own X (or it failed to load).
        dialogRef.current = null;
        readyRef.current = false;
        setApprovingAll(false);
        setMode("closed");
      });
      setMode("dialog");
    });
  }, [onMessage]);

  // Opens over the pane; Expand moves it into a window over Word.
  const open = useCallback(() => {
    if (dialogRef.current) return;
    setMode("overlay");
  }, []);

  // Nothing left: close the window so the writer is back in Word.
  useEffect(() => {
    if (mode !== "closed" && queue.length === 0 && phase === "idle") handlersRef.current.onClose();
  }, [mode, queue.length, phase]);

  const canExpand = dialogsSupported();
  return { mode, state, open, ...handlers, placement: "pane", onResize: canExpand ? openWindow : undefined };
}
